import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { endOfDay } from "date-fns";

import { AssignmentPriority, CreateAssignmentRequest, assignmentsApi } from "@/api";
import { AssignmentFormValues } from "@/common/components/assignments/shared/AssignmentForm.types";
import { useAssignmentFormOptions } from "@/common/components/assignments/shared/hooks/useAssignmentFormOptions";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAssignmentCreation(
  onClose: () => void,
  clientId?: string | null,
  caseNoteId?: string | null,
  defaultDescription?: string
) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUserContext();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const { priorityOptions, validationSchema } = useAssignmentFormOptions();

  const createAssignmentMutation = useMutation({
    mutationFn: (request: CreateAssignmentRequest) =>
      assignmentsApi.createAssignment({ createAssignmentRequest: request }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.creationDialog.successfullyAdded"));
      onClose();
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  const formik = useFormikWithDirtyForm<AssignmentFormValues>({
    initialValues: {
      dueDate: new Date(),
      description: defaultDescription ?? "",
      priority: AssignmentPriority.Normal,
      assignedCounselorId: currentUser.counselorId ?? null,
      assignedClientId: clientId ?? null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const request: CreateAssignmentRequest = {
        dueDate: endOfDay(values.dueDate),
        description: values.description,
        priority: values.priority,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        assignedCounselorId: values.assignedCounselorId!,
        assignedClientId: values.assignedClientId,
        assignedCaseNoteId: caseNoteId ?? null,
      };

      try {
        await createAssignmentMutation.mutateAsync(request);
      } catch {
        // Reported by the mutation's error handler; the dialog stays open so the input is kept.
      }
    },
  });

  return { formik, priorityOptions };
}
